import { createFileRoute, Link } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { clientRequestHeaders } from "@/lib/sm-api";
import { OpsTenantCombo } from "@/components/admin/OpsTenantCombo";
import { OpsSubnav } from "@/components/OpsSubnav";
import { friendlyEventLabel } from "@/lib/ops-event-labels";
import { RefreshCw } from "lucide-react";
import type { OpsEventRow, OpsSeverity } from "@/lib/ops-types";

export const Route = createFileRoute("/admin/ops/events")({
  component: OpsEventsPage,
  head: () => ({ meta: [{ title: "Eventos — Operaciones" }] }),
});

const PAGE_SIZE = 50;
const SEV_CLASS: Record<OpsSeverity, string> = {
  info:  "bg-sky-500/15 text-sky-300 border-sky-500/20",
  warn:  "bg-amber-500/15 text-amber-300 border-amber-500/20",
  error: "bg-red-500/15 text-red-300 border-red-500/20",
  debug: "bg-secondary/50 text-muted-foreground border-border",
};

function OpsEventsPage() {
  const [rows, setRows]         = useState<OpsEventRow[]>([]);
  const [loading, setLoading]   = useState(false);
  const [hasMore, setHasMore]   = useState(true);
  const [q, setQ]               = useState("");
  const [severity, setSeverity] = useState("all");
  const [tenantId, setTenantId] = useState("");
  const limitRef = useRef(PAGE_SIZE);

  const fetchPage = useCallback(async (reset: boolean) => {
    setLoading(true);
    try {
      limitRef.current = reset ? PAGE_SIZE : limitRef.current + PAGE_SIZE;
      const params = new URLSearchParams({ limit: String(limitRef.current) });
      if (q.trim())           params.set("q", q.trim());
      if (severity !== "all") params.set("severity", severity);
      const hdrs = tenantId
        ? { ...clientRequestHeaders(tenantId) }
        : { ...clientRequestHeaders(), "X-Tenant-Id": "" };
      const res = await fetch(`/api/ops/events?${params}`, { credentials: "include", headers: hdrs });
      if (!res.ok) return;
      const data = (await res.json()) as { events?: OpsEventRow[] };
      const newRows = data.events ?? [];
      setRows(newRows);
      setHasMore(newRows.length === limitRef.current);
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [q, severity, tenantId]);

  useEffect(() => { void fetchPage(true); }, []); // eslint-disable-line

  const sevBadge = (s: string) => (
    <Badge className={`text-[10px] border ${SEV_CLASS[s as OpsSeverity] ?? SEV_CLASS.info}`}>{s}</Badge>
  );

  return (
    <div className="p-4 sm:p-6 space-y-4">
      <OpsSubnav hint="Eventos crudos — para investigación agrupada usá Errors o Traces." />
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-lg font-bold text-foreground">Eventos operacionales</h1>
          <p className="text-xs text-muted-foreground">{rows.length} eventos · sm_platform_events + sm_business_events + sm_audit_logs</p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void fetchPage(true)} disabled={loading} className="gap-2">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Actualizar
        </Button>
      </div>

      <div className="glass-card rounded-xl p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <Label className="text-xs">Buscar</Label>
            <Input className="mt-1 h-8 bg-secondary/50 text-xs" placeholder="tipo, mensaje, entidad…"
              value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") void fetchPage(true); }} />
          </div>
          <div>
            <Label className="text-xs">Severidad</Label>
            <Select value={severity} onValueChange={setSeverity}>
              <SelectTrigger className="mt-1 h-8 bg-secondary/50 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="info">info</SelectItem>
                <SelectItem value="warn">warn</SelectItem>
                <SelectItem value="error">error</SelectItem>
                <SelectItem value="debug">debug</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <OpsTenantCombo value={tenantId} onChange={setTenantId} />
          <div className="flex items-end">
            <Button size="sm" className="w-full h-8 text-xs" onClick={() => void fetchPage(true)} disabled={loading}>Aplicar filtros</Button>
          </div>
        </div>
      </div>

      <div className="glass-card overflow-x-auto rounded-xl">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-36">Fecha</TableHead>
              <TableHead className="w-20">Sev.</TableHead>
              <TableHead className="w-52">Evento</TableHead>
              <TableHead>Detalle</TableHead>
              <TableHead className="w-32">Entidad</TableHead>
              <TableHead className="w-28">Tenant</TableHead>
              <TableHead className="w-16 text-right">ms</TableHead>
              <TableHead className="w-24">Request</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && !loading ? (
              <TableRow><TableCell colSpan={8} className="py-12 text-center text-xs text-muted-foreground">Sin eventos. Usá "Actualizar".</TableCell></TableRow>
            ) : null}
            {rows.map((e) => (
              <TableRow key={e.id} className={e.severity === "error" ? "bg-red-500/5" : e.severity === "warn" ? "bg-amber-500/5" : ""}>
                <TableCell className="whitespace-nowrap text-[11px] text-muted-foreground">{new Date(e.createdAt).toLocaleString("es-AR")}</TableCell>
                <TableCell>{sevBadge(e.severity)}</TableCell>
                <TableCell className="text-xs font-medium">{friendlyEventLabel(e.type)}</TableCell>
                <TableCell className="max-w-[280px] truncate text-xs text-muted-foreground" title={e.message}>{e.message}</TableCell>
                <TableCell className="max-w-[120px] truncate text-[11px] text-muted-foreground">{e.functionName}</TableCell>
                <TableCell className="font-mono text-[10px] text-muted-foreground">{e.tenantId?.slice(0, 12) ?? "—"}</TableCell>
                <TableCell className="text-right text-[11px] text-muted-foreground">{e.durationMs ?? "—"}</TableCell>
                <TableCell className="font-mono text-[10px]">
                  {e.requestId ? (
                    <Link to="/admin/ops/traces" search={{ requestId: e.requestId } as never} className="text-primary hover:underline">{e.requestId.slice(0, 8)}…</Link>
                  ) : "—"}
                </TableCell>
              </TableRow>
            ))}
            {loading ? <TableRow><TableCell colSpan={8} className="py-4 text-center text-xs text-muted-foreground">Cargando…</TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </div>

      {hasMore && !loading ? (
        <div className="flex justify-center pt-2">
          <Button variant="outline" size="sm" onClick={() => void fetchPage(false)}>Cargar más</Button>
        </div>
      ) : null}
    </div>
  );
}
